import { Link } from "react-router-dom";
import { useReveal } from "@/lib/hooks";
import { Hotel, Train, Car, Bus, ArrowRight } from "lucide-react";

const travelOptions = [
  {
    icon: Bus,
    title: "By Bus",
    body: "Citylink bus services run from Dublin City to Galway Coach Station (≈ 2 hr 30) — a 15-minute walk from the ceremony.",
  },
  {
    icon: Train,
    title: "By Train",
    body: "Direct Iarnród Éireann services from Dublin Heuston to Galway Ceannt (≈ 2 hr 30). St. Joseph's Church is a 15-minute walk from the train station.",
  },
  {
    icon: Car,
    title: "By Car",
    body: (
      <>
        M6 motorway from Dublin (≈ 2 hr 15). Complimentary parking is available at
        Glenlo Abbey — for the ceremony in Galway city, see the parking options
        listed in the{" "}
        <a
          href="#schedule"
          className="text-[#6A1E2D] underline underline-offset-4 decoration-[#6A1E2D]/40 hover:decoration-[#6A1E2D] transition"
        >
          Ceremony section of the schedule
        </a>
        .
      </>
    ),
  },
];

export default function TravelAccommodation() {
  const headerRef = useReveal();

  return (
    <section
      id="travel"
      data-testid="travel-section"
      className="bg-[#FAF9F6] pt-16 md:pt-24 pb-6 md:pb-8 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className="reveal max-w-3xl mb-10 md:mb-12">
          <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-4">
            Travel & Stay
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#1A1818] leading-[1.05] mb-6">
            Make a weekend of it.
          </h2>
          <p className="font-body text-[#4A4747] leading-relaxed">
            The main wedding events are all on the Friday. If you're still
            around on the Saturday, we'll likely round up whoever's about for
            something casual. Galway is a brilliant little city to wander,
            full of pubs, seafood and live trad music — perfect for making a
            weekend of it.
          </p>
        </div>

        {/* Travel options bento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {travelOptions.map((opt) => {
            const Icon = opt.icon;
            return (
              <div
                key={opt.title}
                data-testid={`travel-option-${opt.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[#F3EFE9] p-8 border border-[#A3C1DA]/30"
              >
                <Icon className="w-5 h-5 text-[#6A1E2D] mb-4" strokeWidth={1.4} />
                <h3 className="font-display text-2xl text-[#1A1818] mb-2">{opt.title}</h3>
                <p className="text-sm text-[#4A4747] leading-relaxed">{opt.body}</p>
              </div>
            );
          })}
        </div>

        {/* Accommodation CTA card */}
        <Link
          to="/accommodation"
          data-testid="accommodation-cta"
          className="group block bg-[#6A1E2D] hover:bg-[#521723] transition-colors duration-300 p-8 md:p-10 text-[#FAF9F6]"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-5">
              <Hotel className="w-7 h-7 text-[#A3C1DA] shrink-0 mt-1" strokeWidth={1.4} />
              <div>
                <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-2">
                  Where to stay
                </p>
                <h3 className="font-display text-3xl md:text-4xl font-light leading-tight mb-2">
                  Recommended places to stay
                </h3>
                <p className="text-sm md:text-base text-[#FAF9F6]/80 leading-relaxed max-w-2xl">
                  We've put together a list of hotels and B&Bs near Glenlo
                  Abbey, from on-site rooms to budget-friendly options in the
                  city. Have a browse and book early — it's a busy weekend.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-3 text-xs tracking-luxe uppercase shrink-0 group-hover:gap-4 transition-all self-start md:self-center">
              View accommodation
              <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}
